/**
 * Open Location Code ("Plus code") ENCODER — a port of Google's reference algorithm
 * (com.google.openlocationcode 1.0.4, the library the be uses for line 3 of the stamped copy).
 *
 * Pure integer arithmetic after one rounding step, exactly as the reference does, so this port and
 * the be agree byte-for-byte; the be's wire dump `sv-stamp-lines.json` (`show.lines[2]`) is the
 * ORACLE the test pins (SPEC_STAMP_ROUND §B.5 / §E.3). No network, no lookup, ~60 lines.
 *
 * A Plus code IS a coordinate to ~14 m (10 characters): the card shows it only where it shows the
 * coordinates (`liveCard = COORDINATES` and `stamp.plusCode`).
 */

const ALPHABET = '23456789CFGHJMPQRVWX';
const SEPARATOR = '+';
const SEPARATOR_POSITION = 8;
const PADDING = '0';
const ENCODING_BASE = ALPHABET.length; // 20
const LATITUDE_MAX = 90;
const LONGITUDE_MAX = 180;
const MAX_DIGIT_COUNT = 15;
const PAIR_CODE_LENGTH = 10;
const GRID_CODE_LENGTH = MAX_DIGIT_COUNT - PAIR_CODE_LENGTH; // 5
const GRID_COLUMNS = 4;
const GRID_ROWS = 5;
/** 8000 · 5^5 and 8000 · 4^5 — the reference's integer multipliers for the finest grid cell. */
const LAT_INTEGER_MULTIPLIER = 8000 * 3125;
const LNG_INTEGER_MULTIPLIER = 8000 * 1024;

/** The full 10-character code (≈ 14 m cell) — what the stamped copy prints. */
export const CODE_PRECISION_NORMAL = 10;

export function clipLatitude(lat: number): number {
  return Math.min(LATITUDE_MAX, Math.max(-LATITUDE_MAX, lat));
}

export function normalizeLongitude(lng: number): number {
  let out = lng;
  while (out < -LONGITUDE_MAX) out += 360;
  while (out >= LONGITUDE_MAX) out -= 360;
  return out;
}

function computeLatitudePrecision(codeLength: number): number {
  if (codeLength <= PAIR_CODE_LENGTH) return ENCODING_BASE ** Math.floor(codeLength / -2 + 2);
  return ENCODING_BASE ** -3 / GRID_ROWS ** (codeLength - PAIR_CODE_LENGTH);
}

/** Encode a fix. Throws on a non-finite input or an invalid length (2, 4, 6, 8, 10 … 15). */
export function encodePlusCode(latitude: number, longitude: number, codeLength = CODE_PRECISION_NORMAL): string {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new RangeError('plus code: latitude and longitude must be finite');
  if (codeLength < 2 || (codeLength < SEPARATOR_POSITION && codeLength % 2 === 1)) throw new RangeError(`plus code: invalid length ${codeLength}`);
  const length = Math.min(codeLength, MAX_DIGIT_COUNT);
  let lat = clipLatitude(latitude);
  const lng = normalizeLongitude(longitude);
  // 90° north must encode to a decodable cell: nudge it just south of the pole.
  if (lat === LATITUDE_MAX) lat -= 0.9 * computeLatitudePrecision(length);

  // The reference: multiply by the finest precision, round at 1e-6 to shed floating error, truncate.
  let latVal = Math.floor(Math.round((lat + LATITUDE_MAX) * LAT_INTEGER_MULTIPLIER * 1e6) / 1e6);
  let lngVal = Math.floor(Math.round((lng + LONGITUDE_MAX) * LNG_INTEGER_MULTIPLIER * 1e6) / 1e6);

  let code = '';
  if (length > PAIR_CODE_LENGTH) {
    for (let i = 0; i < GRID_CODE_LENGTH; i += 1) {
      const latDigit = latVal % GRID_ROWS;
      const lngDigit = lngVal % GRID_COLUMNS;
      code = ALPHABET.charAt(latDigit * GRID_COLUMNS + lngDigit) + code;
      latVal = Math.floor(latVal / GRID_ROWS);
      lngVal = Math.floor(lngVal / GRID_COLUMNS);
    }
  } else {
    latVal = Math.floor(latVal / GRID_ROWS ** GRID_CODE_LENGTH);
    lngVal = Math.floor(lngVal / GRID_COLUMNS ** GRID_CODE_LENGTH);
  }
  for (let i = 0; i < PAIR_CODE_LENGTH / 2; i += 1) {
    code = ALPHABET.charAt(lngVal % ENCODING_BASE) + code;
    code = ALPHABET.charAt(latVal % ENCODING_BASE) + code;
    latVal = Math.floor(latVal / ENCODING_BASE);
    lngVal = Math.floor(lngVal / ENCODING_BASE);
    if (i === 0) code = SEPARATOR + code;
  }
  if (length < SEPARATOR_POSITION) {
    code = code.slice(0, length) + PADDING.repeat(SEPARATOR_POSITION - length) + code.slice(SEPARATOR_POSITION);
  }
  return code.slice(0, Math.max(SEPARATOR_POSITION + 1, length + 1));
}

/** The shape of a full code, for guards and tests (8 digits, `+`, 2 digits). */
export const PLUS_CODE_FULL = /^[23456789CFGHJMPQRVWX]{8}\+[23456789CFGHJMPQRVWX]{2}$/;
